import { api } from 'API';
import { addImposterToUrl } from 'Utils';
import { ROUTES } from 'Config/routes';
import {
  Committee,
  CommitteeDetails,
  IRecordingDto,
  IDocumentDto,
  IUnscheduledCommitteeDetails,
  ICommitteesResponse,
  StateContentType,
} from 'Interfaces/MedicalCommittees';
import { IStateContentResponse } from 'Interfaces/Umbraco/MedicalCommittees';

const committeesBaseUrl = '/medical-committees';
const contactsBaseUrl = '/medical-committees/contacts';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const recordingsUrl = ROUTES.MEDICAL_COMMITTEES_RECORDINGS; // Used in commented API calls for future implementation
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const contactsUrl = ROUTES.MEDICAL_COMMITTEES_CONTACTS; // Used in commented API calls for future implementation

export interface IPhonePrefixDTO {
  isMobile: boolean;
  id: number;
  value: string;
}

export interface IAdditionalContactDTO {
  id: number;
  ownerCaseNumber: string;
  firstName: string;
  lastName: string;
  email: string;
  phonePrefix?: IPhonePrefixDTO;
  phoneNumber: string;
}

export interface IContactUpsertDto {
  FirstName: string;
  LastName: string;
  Email: string;
  PhonePrefixId: number;
  PhoneNumber: string;
  IsApproved: boolean;
}

export interface IAdditionalContactResult {
  additionalContact: IAdditionalContactDTO;
  isApproved: boolean;
}

export interface IStepItem {
  title: string;
  date?: Date;
  description: string;
  isActive?: boolean;
  externalLink?: string;
}

export interface IProcessTimeline {
  steps: IStepItem[];
}

export const getAdditionalContact = async (): Promise<
  IAdditionalContactResult | undefined
> => {
  try {
    const response = await api.get<IAdditionalContactDTO | null>(
      addImposterToUrl(contactsBaseUrl, true),
    );
    if (!response.data) {
      return undefined;
    }
    return { additionalContact: response.data, isApproved: true };
  } catch (error) {
    console.error('Failed to fetch additional contact:', error);
    return undefined;
  }
};

export const addAdditionalContact = async (
  body: IContactUpsertDto,
): Promise<IAdditionalContactResult | undefined> => {
  try {
    const response = await api.post<IAdditionalContactDTO>(
      addImposterToUrl(contactsBaseUrl, true),
      body,
    );
    return response.data
      ? { additionalContact: response.data, isApproved: true }
      : undefined;
  } catch (error) {
    console.error('Failed to add additional contact:', error);
    throw error;
  }
};

export const updateAdditionalContact = async (
  contactId: number,
  body: IContactUpsertDto,
): Promise<IAdditionalContactResult | undefined> => {
  try {
    const response = await api.put<IAdditionalContactDTO>(
      addImposterToUrl(`${contactsBaseUrl}/${contactId}`, true),
      body,
    );
    return response.data
      ? { additionalContact: response.data, isApproved: true }
      : undefined;
  } catch (error) {
    console.error('Failed to update additional contact:', error);
    throw error;
  }
};

export const deleteAdditionalContact = async (
  contactId: number,
): Promise<boolean> => {
  try {
    await api.delete(addImposterToUrl(`${contactsBaseUrl}/${contactId}`, true));
    return true;
  } catch (error) {
    console.error('Failed to delete additional contact:', error);
    return false;
  }
};

export const getCommittees = async (): Promise<
  ICommitteesResponse | undefined
> => {
  try {
    const response = await api.get<ICommitteesResponse>(
      addImposterToUrl(committeesBaseUrl, true),
    );
    return response.data || undefined;
  } catch (error) {
    console.error('Failed to fetch committees:', error);
    return undefined;
  }
};

export const getUnscheduledCommittees = async (): Promise<
  Committee[] | undefined
> => {
  try {
    const response = await api.get<Committee[]>(
      addImposterToUrl(`${committeesBaseUrl}/unscheduled`, true),
    );
    return response.data || undefined;
  } catch (error) {
    console.error('Failed to fetch unscheduled committees:', error);
    return undefined;
  }
};

export const getCommitteeById = async (
  id: string,
): Promise<CommitteeDetails | undefined> => {
  try {
    const response = await api.get<CommitteeDetails>(
      addImposterToUrl(`${committeesBaseUrl}/${id}`, true),
    );
    return response.data || undefined;
  } catch (error) {
    console.error(`Failed to fetch committee with id ${id}:`, error);
    return undefined;
  }
};

// New API function for recordings based on server structure
export const getRecordings = async (): Promise<IRecordingDto[] | undefined> => {
  try {
    const response = await api.get<IRecordingDto[]>(
      addImposterToUrl(recordingsUrl, true),
    );
    return response.data || undefined;
  } catch (error) {
    console.error('Failed to fetch recordings:', error);
    return undefined;
  }
};

export const downloadRecording = async (
  documentNumber: string,
): Promise<IDocumentDto | undefined> => {
  try {
    const response = await api.get<IDocumentDto>(
      addImposterToUrl(`${recordingsUrl}/${documentNumber}`, true),
    );
    return response.data || undefined;
  } catch (error) {
    console.error(`Failed to download recording ${documentNumber}:`, error);
    return undefined;
  }
};

export const getUnscheduledCommitteeDetails = async (): Promise<
  IUnscheduledCommitteeDetails | undefined
> => {
  try {
    const response = await api.get<IUnscheduledCommitteeDetails>(
      addImposterToUrl(`${committeesBaseUrl}/unscheduled`, true),
    );
    return response.data || undefined;
  } catch (error) {
    console.error('Failed to fetch unscheduled committee details:', error);
    return undefined;
  }
};

export const getStateContent = async (
  stateContentId: string,
  committeeLocationId?: number,
): Promise<IStateContentResponse | undefined> => {
  try {
    let url = `${committeesBaseUrl}/state-content/${stateContentId}`;

    // Send committeeLocationId only when stateContentType is WithLocation
    const shouldAddLocationParam =
      committeeLocationId &&
      stateContentId === StateContentType.WithLocation.toString();

    if (shouldAddLocationParam) {
      url += `?committeeLocationId=${committeeLocationId}`;
    }

    const response = await api.get<IStateContentResponse>(
      addImposterToUrl(url, !shouldAddLocationParam),
    );
    return response.data || undefined;
  } catch (error) {
    console.error(
      `Failed to fetch state content for id ${stateContentId}:`,
      error,
    );
    return undefined;
  }
};

export const downloadDocument = async (
  documentNumber: string,
): Promise<IDocumentDto | undefined> => {
  try {
    const response = await api.get<IDocumentDto>(
      addImposterToUrl(`${committeesBaseUrl}/document/${documentNumber}`, true),
    );
    return response.data || undefined;
  } catch (error) {
    console.error(`Failed to download document ${documentNumber}:`, error);
    return undefined;
  }
};
